import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';

import MaterialSymbol from '#/components/basic/icons/MaterialSymbol';
import { renderSimpleMarkdownMessage } from '#/lib/display/markdown';

const Wrapper = styled.div<{
  $marginTop?: boolean | number;
  $marginBottom?: boolean | number;
  $nightMode?: boolean;
  $noBackground?: boolean;
}>`
  padding: 12px 16px;
  min-height: 48px;
  display: flex;
  align-items: center;
  ${props =>
    props.$noBackground
      ? ''
      : `background: ${
          props.$nightMode ? 'var(--red1)' : 'rgba(210, 62, 112, 0.08)'
        };`}
  color: ${props => (props.$nightMode ? 'var(--red3b)' : '#922248')};
  margin-top: ${({ $marginTop }) => ($marginTop ? `${$marginTop}px` : '0')};
  margin-bottom: ${({ $marginBottom }) =>
    !$marginBottom
      ? 0
      : typeof $marginBottom === 'number'
      ? `${$marginBottom}px`
      : '20px'};
  text-align: left;
  line-height: 1.5;
  border-radius: 5px;
`;

const Flex = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  text-align: left;
`;

interface PanelProps {
  message: string | null;
  disableAutoScroll?: boolean;
  disableWarningIcon?: boolean;

  marginTop?: false | number;
  marginBottom?: boolean | number;
  nightMode?: boolean;
  noBackground?: boolean;
}

const ErrorPanel = ({
  message,
  noBackground,
  nightMode = true,
  marginTop = false,
  marginBottom = false,
  disableAutoScroll = false,
  disableWarningIcon = false,
}: PanelProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (disableAutoScroll || !message) {
      return;
    }
    // Make sure error is scrolled to when appeared/changed
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [disableAutoScroll, message]);

  return message ? (
    <>
      <div ref={scrollRef} />
      <Wrapper
        $marginTop={marginTop}
        $marginBottom={marginBottom}
        $nightMode={nightMode}
        $noBackground={noBackground}
      >
        {disableWarningIcon ? (
          <>{renderSimpleMarkdownMessage(message, 'red')}</>
        ) : (
          <Flex>
            <MaterialSymbol icon="warning" fontSize="small" rounded />
            <div>{renderSimpleMarkdownMessage(message, 'red')}</div>
          </Flex>
        )}
      </Wrapper>
    </>
  ) : null;
};

export default ErrorPanel;
